import { createSignal, createResource, createMemo, createEffect, For, Show } from 'solid-js';
import { useSearchParams } from '@solidjs/router';
import Text from '../components/Text';
import PaymentsNav from '../components/PaymentsNav';
import PlayerHeadAvatar from '../components/PlayerHeadAvatar';
import PaymentEntryModal from '../components/payments/PaymentEntryModal';
import ScheduledPaymentEditor from '../components/payments/ScheduledPaymentEditor';
import { Badge } from '../components/Badge';
import { Pagination } from '../components/Pagination';
import { DateRangePills } from '../components/DateRangePills';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { useDebounced } from '../hooks/useDebounced';
import { formatDateTime } from '../services/dateFormat';
import { formatCurrencyExact } from '../services/numberFormat';
import { usePaymentChanges } from '../hooks/usePaymentChanges';
import { PAYMENT_SOURCES, PAYMENT_STATUSES, PAYMENT_STATUS_TONE, balanceChangeLabelKey, entryIcon, isBalanceChange, signedAmount } from '../services/payments';
import type { PaymentLedgerEntryDto, PaymentLedgerPageDto, PaymentSummaryDto } from '../api';
import sellbot from './Sellbot.module.css';
import styles from '../components/payments/Payments.module.css';

type KindFilter = 'payments' | 'balance' | 'all';
type QuickFilter = 'all' | 'today' | '7d' | '30d' | 'custom';

const QUICK_PILLS = [
    { value: 'all' as QuickFilter, labelKey: 'DATE_RANGE_ALL' },
    { value: 'today' as QuickFilter, labelKey: 'DATE_RANGE_TODAY' },
    { value: '7d' as QuickFilter, labelKey: 'DATE_RANGE_7D' },
    { value: '30d' as QuickFilter, labelKey: 'DATE_RANGE_30D' },
    { value: 'custom' as QuickFilter, labelKey: 'SELLBOT_STATS_RANGE_CUSTOM' },
];

const KIND_PILLS = [
    { value: 'all' as KindFilter, labelKey: 'PAYMENTS_KIND_FILTER_ALL' },
    { value: 'payments' as KindFilter, labelKey: 'PAYMENTS_KIND_FILTER_PAYMENTS' },
    { value: 'balance' as KindFilter, labelKey: 'PAYMENTS_KIND_FILTER_BALANCE' },
];

const SKELETON_ROW_COUNT = 8;
const PAGE_SIZE = 25;

export default function Payments() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const [searchParams] = useSearchParams();

    const [quickFilter, setQuickFilter] = createSignal<QuickFilter>('all');
    const [customFrom, setCustomFrom] = createSignal('');
    const [customTo, setCustomTo] = createSignal('');
    const [kindFilter, setKindFilter] = createSignal<KindFilter>('all');
    const [directionFilter, setDirectionFilter] = createSignal('');
    const [sourceFilter, setSourceFilter] = createSignal('');
    const [statusFilter, setStatusFilter] = createSignal('');
    const [search, setSearch] = createSignal(typeof searchParams.player === 'string' ? searchParams.player : '');
    const debouncedSearch = useDebounced(search, 250);
    const [page, setPage] = createSignal(0);
    const [openEntryId, setOpenEntryId] = createSignal<string | null>(null);
    const [editorOpen, setEditorOpen] = createSignal(false);

    const range = createMemo((): { from?: string; to?: string } => {
        const now = new Date();
        switch (quickFilter()) {
            case 'today': return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString() };
            case '7d': return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() };
            case '30d': return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() };
            case 'custom': return {
                from: customFrom() ? new Date(`${customFrom()}T00:00:00`).toISOString() : undefined,
                to: customTo() ? new Date(`${customTo()}T23:59:59.999`).toISOString() : undefined,
            };
            default: return {};
        }
    });

    const [feed, { refetch: refetchFeed }] = createResource(
        () => {
            const id = botId();
            if (!id) return false;
            return {
                id, pageSize: PAGE_SIZE, page: page(), kind: kindFilter(), direction: directionFilter(), source: sourceFilter(),
                status: statusFilter(), search: debouncedSearch().trim(), ...range(),
            };
        },
        async (params) => {
            const res = await api.payments.ledger(params.id, {
                page: params.page + 1,
                pageSize: params.pageSize,
                kind: params.kind === 'all' ? undefined : params.kind,
                direction: params.direction || undefined,
                source: params.source || undefined,
                status: params.status || undefined,
                search: params.search || undefined,
                from: params.from,
                to: params.to,
            });
            if (isOk(res.data)) return res.data.value as PaymentLedgerPageDto;
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_LOAD_FAILED'));
            return { total: 0, page: 1, pageSize: params.pageSize, entries: [] } satisfies PaymentLedgerPageDto;
        },
    );

    const [summary, { refetch: refetchSummary }] = createResource(
        () => {
            const id = botId();
            return id ? { id, search: debouncedSearch().trim(), ...range() } : false;
        },
        async (params) => {
            const res = await api.payments.summary(params.id, { search: params.search || undefined, from: params.from, to: params.to });
            return isOk(res.data) ? res.data.value as PaymentSummaryDto : undefined;
        },
    );

    const totalPages = createMemo(() => {
        const feedPage = feed.latest;
        return feedPage ? Math.max(1, Math.ceil(feedPage.total / feedPage.pageSize)) : 1;
    });

    createEffect(() => {
        const lastPage = totalPages() - 1;
        if (feed.latest && page() > lastPage) setPage(lastPage);
    });

    const refresh = () => {
        void refetchFeed();
        void refetchSummary();
    };

    usePaymentChanges(botId, refresh);

    const resetPageAfter = <T,>(setter: (value: T) => void) => (value: T) => { setter(value); setPage(0); };

    const filterToPlayer = (playerName: string) => {
        setSearch(playerName);
        setPage(0);
    };

    const showReview = () => {
        setKindFilter('payments');
        setStatusFilter('Unknown');
        setPage(0);
    };

    const rowLabel = (entry: PaymentLedgerEntryDto) => {
        return isBalanceChange(entry) ? t(balanceChangeLabelKey(entry)) : null;
    };

    const paymentCount = (count: number) => count === 1 ? t('PAYMENTS_SUMMARY_COUNT_ONE') : t('PAYMENTS_SUMMARY_COUNT', count);

    const money = (value: number | null | undefined) => value == null ? '--' : formatCurrencyExact(value);

    return (
        <div class={sellbot.container}>
            <PaymentsNav />
            <div class={sellbot.headerRow}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1><Text val="PAYMENTS_TITLE" /></h1>
                    <span class={sellbot.heroSubtitle}><Text val="PAYMENTS_HISTORY_SUBTITLE" /></span>
                </div>
                <button type="button" class="btn btn-primary" disabled={!botId()} onClick={() => setEditorOpen(true)}>
                    <i class="fa-solid fa-paper-plane" /> <Text val="PAYMENTS_SEND" />
                </button>
            </div>

            <div class={styles.summaryGrid}>
                <div class="fcard"><span class={sellbot.statLabel}><i class="fa-solid fa-wallet" /> <Text val="PAYMENTS_SUMMARY_CASH" /></span><span class={sellbot.statValue}>{money(summary()?.cash)}</span></div>
                <div class="fcard"><span class={sellbot.statLabel}><i class="fa-solid fa-building-columns" /> <Text val="PAYMENTS_SUMMARY_BANK" /></span><span class={sellbot.statValue}>{money(summary()?.bank)}</span></div>
                <div class="fcard">
                    <span class={sellbot.statLabel}><i class="fa-solid fa-arrow-down" /> <Text val="PAYMENTS_SUMMARY_IN" /></span>
                    <span class={`${sellbot.statValue} ${styles.amountIn}`}>+{money(summary()?.incoming ?? 0)}</span>
                    <span class={styles.statCaption}>{paymentCount(summary()?.incomingCount ?? 0)}</span>
                </div>
                <div class="fcard">
                    <span class={sellbot.statLabel}><i class="fa-solid fa-arrow-up" /> <Text val="PAYMENTS_SUMMARY_OUT" /></span>
                    <span class={`${sellbot.statValue} ${styles.amountOut}`}>-{money(summary()?.outgoing ?? 0)}</span>
                    <span class={styles.statCaption}>{paymentCount(summary()?.outgoingCount ?? 0)}</span>
                </div>
                <div class="fcard">
                    <span class={sellbot.statLabel}><i class="fa-solid fa-scale-balanced" /> <Text val="PAYMENTS_SUMMARY_NET" /></span>
                    <span class={`${sellbot.statValue} ${(summary()?.net ?? 0) < 0 ? styles.amountOut : styles.amountIn}`}>
                        {(summary()?.net ?? 0) < 0 ? '-' : '+'}{money(Math.abs(summary()?.net ?? 0))}
                    </span>
                    <Show when={debouncedSearch().trim()}>
                        {(player) => <span class={styles.statCaption}>{t('PAYMENTS_SUMMARY_WITH_PLAYER', player())}</span>}
                    </Show>
                </div>
                <div class="fcard">
                    <span class={sellbot.statLabel}><i class="fa-solid fa-calendar-days" /> <Text val="PAYMENTS_SUMMARY_DUE" /></span>
                    <span class={sellbot.statValue}>{money(summary()?.dueSoonAmount ?? 0)}</span>
                    <span class={styles.statCaption}>{paymentCount(summary()?.dueSoonCount ?? 0)}</span>
                </div>
            </div>

            <Show when={(summary()?.reviewCount ?? 0) > 0}>
                <div class={styles.reviewBanner}>
                    <i class="fa-solid fa-triangle-exclamation" />
                    <span>{summary()!.reviewCount === 1 ? t('PAYMENTS_REVIEW_BANNER_ONE') : t('PAYMENTS_REVIEW_BANNER', summary()!.reviewCount)}</span>
                    <button type="button" class="btn btn-secondary btn-sm" onClick={showReview}><Text val="PAYMENTS_REVIEW_SHOW" /></button>
                </div>
            </Show>

            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                <div class="h hcenter" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                    <input
                        type="text"
                        class={styles.searchInput}
                        placeholder={t('PAYMENTS_SEARCH_PLACEHOLDER')}
                        value={search()}
                        onInput={(e) => resetPageAfter(setSearch)(e.currentTarget.value)}
                    />
                    <select value={directionFilter()} onChange={(e) => resetPageAfter(setDirectionFilter)(e.currentTarget.value)}>
                        <option value="">{t('PAYMENTS_FILTER_ALL_DIRECTIONS')}</option>
                        <option value="Incoming">{t('PAYMENTS_DIRECTION_INCOMING')}</option>
                        <option value="Outgoing">{t('PAYMENTS_DIRECTION_OUTGOING')}</option>
                    </select>
                    <Show when={kindFilter() !== 'balance'}>
                        <select value={sourceFilter()} onChange={(e) => resetPageAfter(setSourceFilter)(e.currentTarget.value)}>
                            <option value="">{t('PAYMENTS_FILTER_ALL_SOURCES')}</option>
                            <For each={PAYMENT_SOURCES}>{(source) => <option value={source}>{t(`PAYMENTS_SOURCE_${source.toUpperCase()}`)}</option>}</For>
                        </select>
                        <select value={statusFilter()} onChange={(e) => resetPageAfter(setStatusFilter)(e.currentTarget.value)}>
                            <option value="">{t('PAYMENTS_FILTER_ALL_STATUSES')}</option>
                            <For each={PAYMENT_STATUSES}>{(status) => <option value={status}>{t(`PAYMENTS_STATUS_${status.toUpperCase()}`)}</option>}</For>
                        </select>
                    </Show>
                </div>
                <div class="h hcenter" style={{ gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                    <DateRangePills value={kindFilter()} onChange={(kind) => { setKindFilter(kind); setSourceFilter(''); setStatusFilter(''); setPage(0); }} pills={KIND_PILLS} />
                    <DateRangePills value={quickFilter()} onChange={resetPageAfter(setQuickFilter)} pills={QUICK_PILLS} />
                    <Show when={quickFilter() === 'custom'}>
                        <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="date" value={customFrom()} onInput={(e) => resetPageAfter(setCustomFrom)(e.currentTarget.value)} />
                            <span class={sellbot.helper}>-</span>
                            <input type="date" value={customTo()} onInput={(e) => resetPageAfter(setCustomTo)(e.currentTarget.value)} />
                        </div>
                    </Show>
                </div>

                <div class={styles.ledgerList}>
                    <Show when={feed.latest} fallback={
                        <For each={Array.from({ length: SKELETON_ROW_COUNT })}>{() => (
                            <div class={`${sellbot.txnRowMerged} ${sellbot.txnRowSkeleton}`}>
                                <span class={sellbot.txnKind} />
                                <span class={sellbot.badge}>&nbsp;</span>
                                <span class={sellbot.txnPayer}>&nbsp;</span>
                                <span class={sellbot.txnAmount}>&nbsp;</span>
                                <span class={sellbot.txnTime}>&nbsp;</span>
                            </div>
                        )}</For>
                    }>
                        {(feedPage) => (
                            <Show when={feedPage().entries.length > 0} fallback={
                                <div class={sellbot.emptySmall}>
                                    <i class="fa-solid fa-money-bill-transfer" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                                    <p><Text val="PAYMENTS_EMPTY" /></p>
                                    <span class={sellbot.helper}><Text val="PAYMENTS_EMPTY_HINT" /></span>
                                </div>
                            }>
                                <For each={feedPage().entries}>{(entry) => (
                                    <div
                                        class={`${sellbot.txnRowMerged} ${styles.ledgerRow} ${sellbot.clickable}`}
                                        role="button"
                                        tabindex="0"
                                        title={t('PAYMENTS_OPEN_ENTRY')}
                                        onClick={() => setOpenEntryId(entry.id)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') setOpenEntryId(entry.id); }}
                                    >
                                        <span class={`${sellbot.txnKind} ${styles.rowIcon} ${isBalanceChange(entry) ? styles.directionNeutral : signedAmount(entry) < 0 ? styles.directionOut : styles.directionIn}`}>
                                            <i class={`fa-solid ${entryIcon(entry)}`} />
                                        </span>
                                        <span class={styles.rowBadge}><Badge tone={PAYMENT_STATUS_TONE[entry.status] ?? 'neutral'}>
                                            <Text val={isBalanceChange(entry) ? `PAYMENTS_ACCOUNT_${entry.account.toUpperCase()}` : `PAYMENTS_STATUS_${entry.status.toUpperCase()}`} />
                                        </Badge></span>
                                        <span class={`${sellbot.txnPayer} ${styles.rowSubject}`}>
                                            <Show when={entry.playerName} fallback={<span>{rowLabel(entry)}</span>}>
                                                {(playerName) => (
                                                    <>
                                                        <PlayerHeadAvatar name={playerName()} size={20} />
                                                        <button
                                                            type="button"
                                                            class={styles.playerLink}
                                                            title={t('PAYMENTS_FILTER_TO_PLAYER', playerName())}
                                                            onClick={(e) => { e.stopPropagation(); filterToPlayer(playerName()); }}
                                                        >
                                                            {playerName()}
                                                        </button>
                                                    </>
                                                )}
                                            </Show>
                                            <Show when={!isBalanceChange(entry)}>
                                                <span class={sellbot.txnItem}> · <Text val={`PAYMENTS_SOURCE_${entry.source.toUpperCase()}`} /></span>
                                            </Show>
                                            <Show when={entry.reason}>
                                                {(reason) => <span class={sellbot.txnItem} title={t('PAYMENTS_REASON')}> · <i class="fa-solid fa-quote-left" /> {reason()}</span>}
                                            </Show>
                                            <Show when={entry.note}>
                                                {(note) => <span class={`${sellbot.txnItem} ${styles.rowNote}`}> · <i class="fa-solid fa-note-sticky" /> {note()}</span>}
                                            </Show>
                                            <Show when={entry.messageText}>
                                                <span class={sellbot.txnItem} title={entry.messageText!}> · <i class="fa-solid fa-comment" /></span>
                                            </Show>
                                        </span>
                                        <span class={styles.amountCell}>
                                            <span class={`${sellbot.txnAmount} ${signedAmount(entry) < 0 ? styles.amountOut : styles.amountIn}`}>
                                                {signedAmount(entry) < 0 ? '-' : '+'}{formatCurrencyExact(entry.amount)}
                                            </span>
                                            <Show when={entry.balanceAfter != null}>
                                                <span class={styles.balanceAfter}>{t('PAYMENTS_BALANCE_AFTER', formatCurrencyExact(entry.balanceAfter!))}</span>
                                            </Show>
                                        </span>
                                        <span class={`${sellbot.txnTime} ${styles.rowTime}`}>{formatDateTime(entry.createdAt)}</span>
                                    </div>
                                )}</For>
                            </Show>
                        )}
                    </Show>
                </div>
                <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
            </div>

            <Show when={botId()}>
                {(id) => (
                    <>
                        <PaymentEntryModal botId={id()} entryId={openEntryId()} onClose={() => setOpenEntryId(null)} onChanged={refresh} />
                        <ScheduledPaymentEditor
                            open={editorOpen()}
                            botId={id()}
                            editing={null}
                            onClose={() => setEditorOpen(false)}
                            onSaved={() => { setEditorOpen(false); refresh(); }}
                        />
                    </>
                )}
            </Show>
        </div>
    );
}
